'use client'

import Link from 'next/link'

import { Icon } from '@/components/ui/icon'
import { Bone } from '@/components/ui/bone'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import { useCurrency } from '@/hooks/use-currency'
import { ServiceItem } from '@/lib/types/operations/service'
import { Card, CardContent, CardHeader, Chip, Separator } from '@heroui/react'
import { Suspense } from 'react'
import { AddToReservationButton } from './add-to-reservation-button'

import { useServicePricingWithPreferredVehicle } from '@/hooks/use-service-pricing-with-preferred-vehicle'
import { ServiceMediaGallery } from './service-media-gallery'

interface ServiceContentProps {
  service: ServiceItem
}

export function ServiceContent({ service }: ServiceContentProps) {
  const {
    media,
    name,
    description,
    category,
    price,
    labourHours,
    additionalData,
    createdAt
  } = service

  // Currency formatting
  const { format: formatCurrency } = useCurrency()

  // Use preferred vehicle pricing hook directly
  const preferredVehiclePricingData = useServicePricingWithPreferredVehicle(service)

  // Determine price display
  const displayPrice = preferredVehiclePricingData?.calculatedPrice ?? price ?? 0
  const isCalculatedPrice = preferredVehiclePricingData?.hasMultipliers ?? false

  const additionalInfo = additionalData

  // The Economy build is a full_wrap-only option (Coloured Wrap). Surface a link
  // to its explainer so shoppers know the ~30%-cheaper build exists.
  const isFullWrap =
    service.packageConfig?.kind === 'full_wrap' || service.slug === 'color-change-wrap'

  return (
    <div className='space-y-6 w-full pb-24 md:pb-0'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start'>
        {/* Service Images Container */}
        <div>
          <Suspense>
            <ServiceMediaGallery media={media || []} />
          </Suspense>
        </div>

        {/* Primary Service Details */}
        <div className='space-y-4'>
          {(service.isFeatured || service.hasPromotion) && (
            <div className='flex flex-wrap gap-2'>
              {service.isFeatured && (
                <Chip color='warning' variant='soft' startContent={<Icon icon='star-bold' className='w-4 h-4' />}>
                  Featured
                </Chip>
              )}
              {service.hasPromotion && (
                <Chip variant='soft' startContent={<Icon icon='bell' className='w-4 h-4' />}>
                  Promoted
                </Chip>
              )}
            </div>
          )}

          <div className='text-foreground/80'>
            <MarkdownRenderer content={description} variant='service' className='text-lg' />
          </div>

          {/* Trust Badges */}
          <div className='flex flex-wrap gap-2'>
            {additionalInfo?.warranty?.duration && (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                <Icon icon='shield-check' className='w-4 h-4' />
                {additionalInfo.warranty.duration} Warranty
              </span>
            )}
            {additionalInfo?.materialOptions && additionalInfo.materialOptions.length > 0 && (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                <Icon icon='layers-bold-duotone' className='w-4 h-4' />
                {additionalInfo.materialOptions
                  .slice(0, 2)
                  .map((m: any) => m.option)
                  .join(' & ')}
              </span>
            )}
            {category && (
              <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-overlay text-foreground'>
                <Icon icon='tag-horizontal-bold-duotone' className='w-4 h-4' />
                {category.name}
              </span>
            )}
          </div>

          {/* Booking Information Card */}
          <Card className='bg-surface border border-border'>
            <CardHeader className='pb-2'>
              <h3 className='text-lg font-semibold text-foreground'>Booking Information</h3>
            </CardHeader>
            <CardContent className='pt-0'>
              <div className='space-y-3'>
                <div className='flex items-center gap-3'>
                  <Icon icon='clock' className='w-5 h-5' />
                  <span>
                    Duration:{' '}
                    <strong>
                      {Number(labourHours)} {Number(labourHours) === 1 ? 'hour' : 'hours'}
                    </strong>
                  </span>
                  {additionalInfo?.estimatedDuration && (
                    <span className='text-sm text-foreground/70'>({additionalInfo.estimatedDuration})</span>
                  )}
                </div>
                <div className='flex flex-col gap-2'>
                  <div className='flex items-center gap-3'>
                    <Icon icon='dollar-minimalistic-bold-duotone' className='w-5 h-5' />
                    <span>
                      {isCalculatedPrice ? 'Estimated Price' : 'Starting Price'}:
                      <strong> {formatCurrency(displayPrice)}</strong>
                      {preferredVehiclePricingData?.isCalculating && (
                        <Icon icon='spinner-bold' className='w-4 h-4 ml-2 animate-spin inline' />
                      )}
                    </span>
                  </div>
                  {isCalculatedPrice && preferredVehiclePricingData && (
                    <div className='flex items-center gap-2 text-sm text-foreground/70 ml-8'>
                      <span>Base price: {formatCurrency(preferredVehiclePricingData.basePrice)}</span>
                      <Icon icon='arrow-right' className='w-3 h-3' />
                      <span>{formatCurrency(displayPrice)}</span>
                      <Chip size='sm' variant='soft' color='success'>
                        {preferredVehiclePricingData.vehicleMultiplier.toFixed(1)}x
                      </Chip>
                      {preferredVehiclePricingData.preferredVehicleDisplay && (
                        <span className='text-xs'>({preferredVehiclePricingData.preferredVehicleDisplay})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className='w-full'>
            <AddToReservationButton service={service} size='lg' className='w-full' context='detail' />
          </div>

          {isFullWrap && (
            <Link
              href='/economy-wrap'
              className='flex items-center gap-2.5 rounded-xl border border-accent/30 bg-accent/5 px-4 py-3 text-sm transition hover:bg-accent/10'
            >
              <Icon icon='percentage' className='text-lg text-foreground shrink-0' />
              <span className='text-foreground'>
                <strong className='font-semibold'>On a budget?</strong> The Economy build saves about 30%. See what is included.
              </span>
              <Icon icon='arrow-right' className='text-base text-foreground ml-auto shrink-0' />
            </Link>
          )}
        </div>
      </div>

      {/* Service Details: always visible inline sections */}
      {additionalInfo && (
        <div className='space-y-8'>
          <Separator />
          <h2 className='text-2xl font-bold'>Service Details</h2>

          {/* Warranty Information */}
          {additionalInfo.warranty && (
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold flex items-center gap-2'>
                <Icon icon='shield-check' className='w-5 h-5 text-success' />
                Warranty Information
              </h3>
              <div className='space-y-4 pl-7'>
                <div className='flex items-center gap-2'>
                  <Icon icon='book-calendar' className='w-4 h-4 text-primary' />
                  <span className='font-semibold'>Duration: {additionalInfo.warranty.duration}</span>
                </div>
                <div>
                  <h4 className='font-semibold mb-2 flex items-center gap-2'>
                    <Icon icon='check-circle' className='w-4 h-4 text-success' />
                    Coverage Includes:
                  </h4>
                  <ul className='list-disc list-inside space-y-1 text-foreground/80'>
                    {additionalInfo.warranty.coverage?.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
                {additionalInfo.warranty.exclusions && (
                  <div>
                    <h4 className='font-semibold mb-2 flex items-center gap-2'>
                      <Icon icon='close-circle-duotone' className='w-4 h-4 text-danger' />
                      Exclusions:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-foreground/80'>
                      {additionalInfo.warranty.exclusions.map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Prerequisites */}
          {additionalInfo.prerequisites && additionalInfo.prerequisites.length > 0 && (
            <div className='space-y-3'>
              <h3 className='text-lg font-semibold flex items-center gap-2'>
                <Icon icon='list-check-bold-duotone' className='w-5 h-5 text-warning' />
                Prerequisites
              </h3>
              <div className='pl-7'>
                <p className='text-foreground/80 mb-3'>Please ensure the following before your appointment:</p>
                <ul className='space-y-2'>
                  {additionalInfo.prerequisites.map((item: string, index: number) => (
                    <li key={index} className='flex items-start gap-2'>
                      <Icon icon='check-circle-solar' className='w-4 h-4 text-success mt-0.5 shrink-0' />
                      <span className='text-foreground/80'>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Aftercare Instructions */}
          {additionalInfo.aftercare && additionalInfo.aftercare.length > 0 && (
            <div className='space-y-3'>
              <h3 className='text-lg font-semibold flex items-center gap-2'>
                <Icon icon='heart-bold-duotone' className='w-5 h-5 text-danger' />
                Aftercare Instructions
              </h3>
              <div className='pl-7'>
                <p className='text-foreground/80 mb-3'>Follow these instructions to maintain your service:</p>
                <ul className='space-y-2'>
                  {additionalInfo.aftercare.map((item: string, index: number) => (
                    <li key={index} className='flex items-start gap-2'>
                      <Icon icon='info-circle' className='w-4 h-4 text-primary mt-0.5 shrink-0' />
                      <span className='text-foreground/80'>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Variant Options */}
          {additionalInfo.variantOptions && additionalInfo.variantOptions.length > 0 && (
            <div className='space-y-3'>
              <h3 className='text-lg font-semibold flex items-center gap-2'>
                <Icon icon='settings-bold-duotone' className='w-5 h-5 text-secondary' />
                Available Options
              </h3>
              <div className='space-y-4 pl-7'>
                {additionalInfo.variantOptions.map((variant: any, index: number) => (
                  <Card key={index} className='bg-surface border border-border'>
                    <CardContent className='p-4'>
                      <div className='flex justify-between items-start'>
                        <div className='flex-1'>
                          <h4 className='font-semibold text-lg'>{variant.option}</h4>
                          <p className='text-foreground/80 mb-2'>{variant.description}</p>
                          {variant.legalNote && (
                            <p className='text-sm text-warning bg-warning/10 p-2 rounded'>
                              <Icon icon='info-circle' className='w-4 h-4 inline mr-1' />
                              {variant.legalNote}
                            </p>
                          )}
                        </div>
                        {variant.additionalPrice > 0 && (
                          <Chip variant='soft'>
                            +${(variant.additionalPrice / 100).toFixed(2)}
                          </Chip>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Material Options */}
          {additionalInfo.materialOptions && additionalInfo.materialOptions.length > 0 && (
            <div className='space-y-3'>
              <h3 className='text-lg font-semibold flex items-center gap-2'>
                <Icon icon='layers-bold-duotone' className='w-5 h-5 text-primary' />
                Material Options
              </h3>
              <div className='space-y-4 pl-7'>
                {additionalInfo.materialOptions.map((material: any, index: number) => (
                  <Card key={index} className='bg-surface border border-border'>
                    <CardContent className='p-4'>
                      <div className='flex justify-between items-start'>
                        <div className='flex-1'>
                          <h4 className='font-semibold text-lg'>{material.option}</h4>
                          <p className='text-foreground/80 mb-3'>{material.description}</p>
                          {material.benefits && (
                            <div className='space-y-1'>
                              <p className='font-medium text-sm'>Benefits:</p>
                              <ul className='text-sm text-foreground/70 space-y-1'>
                                {material.benefits.map((benefit: string, benefitIndex: number) => (
                                  <li key={benefitIndex} className='flex items-center gap-2'>
                                    <Icon icon='check-circle-solar' className='w-3 h-3 text-success' />
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        {material.additionalPrice > 0 && (
                          <Chip variant='soft'>
                            +${(material.additionalPrice / 100).toFixed(2)}
                          </Chip>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sticky Mobile CTA */}
    </div>
  )
}

export function ServiceContentSkeleton() {
  return (
    <div className="space-y-6 w-full pb-24 md:pb-0">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
        <div>
          <Bone className="aspect-square w-full rounded-large mb-4" />
          <div className="grid grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Bone key={i} className="aspect-square rounded-medium" />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <Bone className="h-8 w-3/4" />
          <Bone className="h-4 w-1/2" />
          <div className="space-y-2">
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-full" />
            <Bone className="h-4 w-3/4" />
          </div>
          <div className="flex gap-4">
            <Bone className="h-4 w-24" />
            <Bone className="h-4 w-24" />
          </div>
          <div className="flex justify-between items-center">
            <Bone className="h-8 w-32" />
            <div className="flex gap-2">
              <Bone className="h-8 w-8 rounded-full" />
              <Bone className="h-8 w-8 rounded-full" />
              <Bone className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

;(ServiceContent as any).Skeleton = ServiceContentSkeleton
